/**
 * Deploy & Operations Help Content
 * 
 * Feature: 136-dashboard-alignment (T052)
 * Pages: /deployments, /maintenance, /reconciliation
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const deployOpsHelp: PageHelpContent = {
  pageTitle: 'Deployments & Operations',
  purpose: 'Track deployment history, manage maintenance windows, and run reconciliation processes to ensure data consistency.',
  sections: [
    {
      title: 'Deployment History',
      paragraphs: [
        'View a timeline of all deployments to the Fabric Control Plane, including application updates, configuration changes, and infrastructure modifications.',
      ],
    },
    {
      title: 'Maintenance Windows',
      paragraphs: [
        'Maintenance windows define scheduled periods when system updates, optimizations, or infrastructure changes can occur with minimal user impact.',
      ],
      concepts: [
        { term: 'Planned Maintenance', definition: 'Scheduled in advance with user notification. Includes capacity resizing, software updates, and optimization runs.' },
        { term: 'Emergency Maintenance', definition: 'Unscheduled maintenance triggered by critical issues. Bypasses normal scheduling procedures.' },
        { term: 'Maintenance Mode', definition: 'System state where non-critical jobs are paused to allow maintenance activities.' },
      ],
    },
    {
      title: 'Reconciliation',
      paragraphs: [
        'Reconciliation compares the Control Plane inventory with live Fabric tenant state to detect drift. Missing or extra resources are flagged for review.',
      ],
      concepts: [
        { term: 'Drift Detection', definition: 'Identifying differences between the expected state (Control Plane inventory) and actual state (Fabric tenant).' },
        { term: 'Auto-Remediation', definition: 'Automatically correcting drift by updating the inventory to match the live tenant state.' },
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Navigate to the Deployments page and check the status of the most recent deployment.' },
        { step: 2, instruction: 'Review the pipeline run history to confirm all stages completed successfully.' },
        { step: 3, instruction: 'Open the Reconciliation tab to trigger a drift detection scan against the live tenant.' },
        { step: 4, instruction: 'Review any flagged drift items and approve or dismiss the recommended remediations.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Infrastructure', href: '/infrastructure' },
        { label: 'Dashboard', href: '/dashboard' },
      ],
    },
  ],
};

export default deployOpsHelp;
